/**
 * SessionManager — registry of named sessions.
 *
 * Lifecycle: create, get, list, delete, rename.
 * Convenience methods for message ops by session ID.
 * Cross-session search and tools.
 */

import type { ToolSet } from "ai";
import { z } from "zod";
import type { CompactResult } from "../utils/compaction-helpers";
import type { WritableContextProvider } from "./context";
import type { StoredCompaction } from "./provider";
import type { SqlProvider } from "./providers/agent";
import type { SearchProvider } from "./search";
import { Session, type SessionContextOptions } from "./session";
import type {
  CompactAfterOptions,
  CompactionErrorHandler,
  SessionMessage,
  SessionTokenCounter
} from "./types";

export interface SessionInfo {
  id: string;
  name: string;
  parent_session_id: string | null;
  model: string | null;
  source: string | null;
  input_tokens: number;
  output_tokens: number;
  estimated_cost: number;
  end_reason: string | null;
  created_at: string;
  updated_at: string;
}

// Pending context entry — resolved per-session with namespaced providers
interface PendingManagerContext {
  label: string;
  options: SessionContextOptions;
}

export interface SessionManagerOptions {}

export class SessionManager {
  private agent!: SqlProvider;
  private _pending: PendingManagerContext[] = [];
  private _cachedPrompt?: WritableContextProvider | true;
  private _compactionFn?:
    | ((messages: SessionMessage[]) => Promise<CompactResult | null>)
    | null;
  private _tokenThreshold?: number;
  private _tokenCounter?: SessionTokenCounter;
  private _compactionErrorHandler?: CompactionErrorHandler;
  private _sessions = new Map<string, Session>();
  private _historyLabel?: string;
  private _tableReady = false;
  private _ready = false;

  constructor(agent: SqlProvider, _options: SessionManagerOptions = {}) {
    this.agent = agent;
    this._ready = true;
    this._ensureTable();
  }

  /**
   * Chainable SessionManager creation with auto-wired context for all sessions.
   *
   * @example
   * ```ts
   * const manager = SessionManager.create(this)
   *   .withContext("soul", { provider: { get: async () => "You are helpful." } })
   *   .withContext("memory", { description: "Learned facts", maxTokens: 1100 })
   *   .withCachedPrompt()
   *   .compactAfter(100_000);
   *
   * // Each getSession(id) auto-creates namespaced providers:
   * //   memory key: "memory_<sessionId>"
   * //   prompt key: "_system_prompt_<sessionId>"
   * const session = manager.getSession("chat-123");
   * ```
   */
  static create(agent: SqlProvider): SessionManager {
    const mgr: SessionManager = Object.create(SessionManager.prototype);
    mgr.agent = agent;
    mgr._pending = [];
    mgr._compactionFn = null;
    mgr._tokenThreshold = undefined;
    mgr._tokenCounter = undefined;
    mgr._compactionErrorHandler = undefined;
    mgr._sessions = new Map();
    mgr._tableReady = false;
    mgr._ready = false;
    return mgr;
  }

  // ── Builder methods ─────────────────────────────────────────────

  withContext(label: string, options?: SessionContextOptions): this {
    this._pending.push({ label, options: options ?? {} });
    return this;
  }

  withCachedPrompt(provider?: WritableContextProvider): this {
    this._cachedPrompt = provider ?? true;
    return this;
  }

  /**
   * Register a compaction function propagated to all sessions.
   * Called by `Session.compact()` to compress message history.
   */
  onCompaction(
    fn: (messages: SessionMessage[]) => Promise<CompactResult | null>
  ): this {
    this._compactionFn = fn;
    return this;
  }

  /**
   * Auto-compact when estimated token count exceeds the threshold.
   * Propagated to all sessions. Requires `onCompaction()`.
   */
  compactAfter(tokenThreshold: number, options?: CompactAfterOptions): this {
    this._tokenThreshold = tokenThreshold;
    if (options?.tokenCounter) {
      this._tokenCounter = options.tokenCounter;
    }
    return this;
  }

  /**
   * Handle failures from automatic compaction in managed sessions.
   */
  onCompactionError(handler: CompactionErrorHandler): this {
    this._compactionErrorHandler = handler;
    return this;
  }

  /**
   * Add a searchable context block that searches conversation history
   * across all sessions managed by this manager.
   *
   * The model can use `search_context` to find relevant messages from
   * any session. The block is readonly (no `set`).
   *
   * @example
   * ```ts
   * SessionManager.create(this)
   *   .withContext("memory", { maxTokens: 1100 })
   *   .withSearchableHistory("history")
   *   .withCachedPrompt();
   * ```
   */
  withSearchableHistory(label: string): this {
    this._historyLabel = label;
    return this;
  }

  // ── Lazy init ───────────────────────────────────────────────────

  private _ensureReady(): void {
    if (this._ready) return;
    this._ready = true;
    this._ensureTable();
  }

  private _ensureTable(): void {
    if (this._tableReady) return;
    this.agent.sql`
      CREATE TABLE IF NOT EXISTS assistant_sessions (
        id TEXT PRIMARY KEY,
        name TEXT NOT NULL,
        parent_session_id TEXT,
        model TEXT,
        source TEXT,
        input_tokens INTEGER DEFAULT 0,
        output_tokens INTEGER DEFAULT 0,
        estimated_cost REAL DEFAULT 0,
        end_reason TEXT,
        created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )
    `;
    this.agent.sql`
      CREATE VIRTUAL TABLE IF NOT EXISTS assistant_fts
      USING fts5(id UNINDEXED, session_id UNINDEXED, role UNINDEXED, content, tokenize='porter unicode61')
    `;
    this._tableReady = true;
  }

  private _createHistoryProvider(): SearchProvider {
    const mgr = this;
    return {
      async get() {
        const sessions = mgr.list();
        if (sessions.length === 0) return null;
        return `${sessions.length} session${sessions.length === 1 ? "" : "s"} available for search.`;
      },
      async search(query: string) {
        const results = mgr.search(query, { limit: 10 });
        if (results.length === 0) return null;
        return results.map((r) => `[${r.role}] ${r.content}`).join("\n---\n");
      }
      // No set — conversation history is readonly
    };
  }

  // ── Session access ────────────────────────────────────────────

  /** Get or create the Session instance for a session ID. */
  getSession(sessionId: string): Session {
    this._ensureReady();
    let session = this._sessions.get(sessionId);
    if (!session) {
      const s = Session.create(this.agent).forSession(sessionId);
      for (const { label, options } of this._pending) {
        s.withContext(label, options);
      }
      if (this._cachedPrompt === true) {
        s.withCachedPrompt();
      } else if (this._cachedPrompt) {
        s.withCachedPrompt(this._cachedPrompt);
      }
      if (this._historyLabel) {
        s.withContext(this._historyLabel, {
          description: "Cross-session conversation history",
          provider: this._createHistoryProvider()
        });
      }
      if (this._compactionFn) {
        s.onCompaction(this._compactionFn);
      }
      if (this._tokenThreshold != null) {
        s.compactAfter(this._tokenThreshold, {
          tokenCounter: this._tokenCounter
        });
      }
      if (this._compactionErrorHandler) {
        s.onCompactionError(this._compactionErrorHandler);
      }
      session = s;
      this._sessions.set(sessionId, session);
    }
    return session;
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  create(
    name: string,
    opts?: { parentSessionId?: string; model?: string; source?: string }
  ): SessionInfo {
    this._ensureReady();
    const id = crypto.randomUUID();
    this.agent.sql`
      INSERT INTO assistant_sessions (id, name, parent_session_id, model, source)
      VALUES (${id}, ${name}, ${opts?.parentSessionId ?? null}, ${opts?.model ?? null}, ${opts?.source ?? null})
    `;
    return this.get(id)!;
  }

  get(sessionId: string): SessionInfo | null {
    this._ensureReady();
    const rows = this.agent.sql`
      SELECT * FROM assistant_sessions WHERE id = ${sessionId}
    ` as unknown as SessionInfo[];
    return rows[0] ?? null;
  }

  list(): SessionInfo[] {
    this._ensureReady();
    return this.agent.sql`
      SELECT * FROM assistant_sessions ORDER BY updated_at DESC
    ` as unknown as SessionInfo[];
  }

  async delete(sessionId: string): Promise<void> {
    await this.getSession(sessionId).clearMessages();
    this.agent.sql`DELETE FROM assistant_sessions WHERE id = ${sessionId}`;
    this._sessions.delete(sessionId);
  }

  rename(sessionId: string, name: string): void {
    this._ensureReady();
    this.agent.sql`
      UPDATE assistant_sessions SET name = ${name}, updated_at = CURRENT_TIMESTAMP
      WHERE id = ${sessionId}
    `;
  }

  // ── Message convenience methods ───────────────────────────────

  async append(
    sessionId: string,
    message: SessionMessage,
    parentId?: string
  ): Promise<string> {
    await this.getSession(sessionId).appendMessage(message, parentId);
    this._touch(sessionId);
    return message.id;
  }

  async upsert(
    sessionId: string,
    message: SessionMessage,
    parentId?: string
  ): Promise<string> {
    const session = this.getSession(sessionId);
    const existing = await session.getMessage(message.id);
    if (existing) {
      await session.updateMessage(message);
    } else {
      await session.appendMessage(message, parentId);
    }
    this._touch(sessionId);
    return message.id;
  }

  async appendAll(
    sessionId: string,
    messages: SessionMessage[],
    parentId?: string
  ): Promise<string | null> {
    const session = this.getSession(sessionId);
    let lastParent = parentId ?? null;
    for (const msg of messages) {
      await session.appendMessage(msg, lastParent);
      lastParent = msg.id;
    }
    this._touch(sessionId);
    return lastParent;
  }

  async getHistory(
    sessionId: string,
    leafId?: string
  ): Promise<SessionMessage[]> {
    return this.getSession(sessionId).getHistory(leafId);
  }

  async getMessageCount(sessionId: string): Promise<number> {
    return this.getSession(sessionId).getPathLength();
  }

  async clearMessages(sessionId: string): Promise<void> {
    await this.getSession(sessionId).clearMessages();
    this._touch(sessionId);
  }

  async deleteMessages(sessionId: string, messageIds: string[]): Promise<void> {
    await this.getSession(sessionId).deleteMessages(messageIds);
    this._touch(sessionId);
  }

  // ── Branching ──────────────────────────────────────────────────

  async getBranches(
    sessionId: string,
    messageId: string
  ): Promise<SessionMessage[]> {
    return this.getSession(sessionId).getBranches(messageId);
  }

  /**
   * Fork a session at a specific message, creating a new session
   * with the history up to that point copied over.
   */
  async fork(
    sessionId: string,
    atMessageId: string,
    newName: string
  ): Promise<SessionInfo> {
    const info = this.create(newName, { parentSessionId: sessionId });
    const history = await this.getSession(sessionId).getHistory(atMessageId);
    const newSession = this.getSession(info.id);

    let parentId: string | null = null;
    for (const msg of history) {
      const newId = crypto.randomUUID();
      const copy: SessionMessage = { ...msg, id: newId };
      await newSession.appendMessage(copy, parentId);
      parentId = newId;
    }

    this._touch(info.id);
    return info;
  }

  // ── Compaction ────────────────────────────────────────────────

  async addCompaction(
    sessionId: string,
    summary: string,
    fromId: string,
    toId: string
  ): Promise<StoredCompaction> {
    return this.getSession(sessionId).addCompaction(summary, fromId, toId);
  }

  async getCompactions(sessionId: string): Promise<StoredCompaction[]> {
    return this.getSession(sessionId).getCompactions();
  }

  async compactAndSplit(
    sessionId: string,
    summary: string,
    newName?: string
  ): Promise<SessionInfo> {
    const old = this.get(sessionId);
    this.agent.sql`
      UPDATE assistant_sessions SET end_reason = 'compaction', updated_at = CURRENT_TIMESTAMP
      WHERE id = ${sessionId}
    `;

    const info = this.create(newName ?? old?.name ?? "Compacted", {
      parentSessionId: sessionId,
      model: old?.model ?? undefined,
      source: old?.source ?? undefined
    });

    await this.append(info.id, {
      id: crypto.randomUUID(),
      role: "assistant",
      parts: [
        { type: "text", text: `[Context from previous session]\n\n${summary}` }
      ]
    });

    return info;
  }

  // ── Usage tracking ────────────────────────────────────────────

  addUsage(
    sessionId: string,
    inputTokens: number,
    outputTokens: number,
    cost: number
  ): void {
    this._ensureReady();
    this.agent.sql`
      UPDATE assistant_sessions SET
        input_tokens = input_tokens + ${inputTokens},
        output_tokens = output_tokens + ${outputTokens},
        estimated_cost = estimated_cost + ${cost},
        updated_at = CURRENT_TIMESTAMP
      WHERE id = ${sessionId}
    `;
  }

  // ── Search ────────────────────────────────────────────────────

  search(query: string, options?: { limit?: number }) {
    this._ensureReady();
    const limit = options?.limit ?? 20;
    // Quote each word individually to prevent FTS5 syntax injection
    // while preserving implicit AND between terms
    const sanitized = query
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => `"${w.replace(/"/g, '""')}"`)
      .join(" ");
    if (!sanitized) return [];
    try {
      return this.agent.sql<{ id: string; role: string; content: string }>`
        SELECT id, role, content FROM assistant_fts
        WHERE assistant_fts MATCH ${sanitized}
        ORDER BY rank LIMIT ${limit}
      `.map((r) => ({
        id: r.id,
        role: r.role,
        content: r.content,
        createdAt: ""
      }));
    } catch {
      return [];
    }
  }

  // ── Tools ─────────────────────────────────────────────────────

  tools(): ToolSet {
    return {
      session_search: {
        description:
          "Search past conversations for relevant context. Searches across all sessions.",
        inputSchema: z.fromJSONSchema({
          type: "object" as const,
          properties: {
            query: { type: "string" as const, description: "Search query" }
          },
          required: ["query"]
        }),
        execute: async ({ query }: { query: string }) => {
          try {
            const results = this.search(query, { limit: 10 });
            if (results.length === 0) return "No results found.";
            return results
              .map((r) => `[${r.role}] ${r.content}`)
              .join("\n---\n");
          } catch (err) {
            return `Error: ${err instanceof Error ? err.message : String(err)}`;
          }
        }
      }
    };
  }

  // ── Internal ──────────────────────────────────────────────────

  private _touch(sessionId: string): void {
    this.agent.sql`
      UPDATE assistant_sessions SET updated_at = CURRENT_TIMESTAMP
      WHERE id = ${sessionId}
    `;
  }
}
